import { useEffect, useState } from 'react';
import { LockReset, ManageAccounts } from '@mui/icons-material';
import { PageBody, Stack, LoadingState } from 'Components/ui/layout';
import type { ActionMenuOption } from 'Components/ui';
import useTranslation from 'Hooks/useTranslation';
import { usePermissionStore } from 'stores';
import rolesApi from 'Api/axios/roles-api';
import type { EmployeeProfileI } from 'types';
import EmployeeHeaderCard from './Components/EmployeeHeaderCard';
import BadgeList from './Components/BadgeList';
import ChangeRoleDrawer from './Components/ChangeRoleDrawer';
import ResetPasswordDrawer from './Components/ResetPasswordDrawer';
import useEmployeeAchievements from './Hooks/useEmployeeAchievements';

interface Props {
  profile: EmployeeProfileI | null;
  loading: boolean;
  isAdmin: boolean;
  onProfileChange: () => void;
}

const hasPermission = (permissions: Array<{ key: string; assigned: boolean }>, lookup: string) =>
  permissions.some(permission => permission.key === lookup && permission.assigned === true);

/**
 * Pestaña "Empleado": ficha e insignias, con las acciones de cuenta.
 *
 * Restablecer la contraseña está disponible para todos; cambiar el tipo de
 * usuario solo para administradores o quien tenga `employee.*`, el mismo gate
 * que protegía la mutación de GraphQL.
 */
const Profile = ({ profile, loading, isAdmin, onProfileChange }: Props) => {
  const { t2 } = useTranslation('EmployeeAssess');
  const permissions = usePermissionStore(state => state.permissions);
  const { achievements, earned, total } = useEmployeeAchievements(profile?.id);

  const [roleOpen, setRoleOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [roles, setRoles] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    rolesApi.readAll().then(response => {
      if (!response.error) {
        setRoles((response.data ?? []).map(role => ({ id: role.id, name: role.name })));
      }
    });
  }, []);

  if (loading || !profile) return <LoadingState>{t2('loading')}</LoadingState>;

  const canChangeRole = isAdmin || hasPermission(permissions, 'employee.*');

  const actions: ActionMenuOption[] = [
    { label: t2('ResetPassword') as string, icon: <LockReset />, onSelect: () => setPasswordOpen(true) },
  ];

  if (canChangeRole) {
    actions.push({
      label: t2('change_user_type') as string,
      icon: <ManageAccounts />,
      onSelect: () => setRoleOpen(true),
    });
  }

  // Sin encabezado: la pestaña de arriba ya dice "Empleado", y la tarjeta
  // siguiente ya trae el nombre y los datos de la persona. Un título que repite
  // la pestaña solo empuja el contenido hacia abajo.
  return (
    <PageBody>
      <Stack>
        <EmployeeHeaderCard
          profile={profile}
          actions={actions}
          badgesLabel={t2('badges_earned', [String(earned), String(total)]) as string}
        />
        <BadgeList achievements={achievements} />
      </Stack>

      <ChangeRoleDrawer
        open={roleOpen}
        employeeId={profile.id}
        currentRoleId={profile.role?.id ?? ''}
        roles={roles}
        onClose={() => setRoleOpen(false)}
        onSaved={onProfileChange}
      />

      <ResetPasswordDrawer
        open={passwordOpen}
        employeeId={profile.id}
        isAdmin={isAdmin}
        onClose={() => setPasswordOpen(false)}
      />
    </PageBody>
  );
};

export default Profile;
